/** D-151: the WhatsApp calls of a contact (contact record, inbox) or of the agency (Calls page):
 * outcome, duration, who took it, « call back on WhatsApp » (wa.me) and « I'll take it ». */

import "./calls.css";

import {
  type Call,
  type CallStatusEnum,
  type CallsListParams,
  useCallsHandledCreate,
  useCallsList,
  useCallsSummaryRetrieve,
} from "@agencyos/api-client";
import { formatPhone } from "@agencyos/core";
import { Badge, type BadgeTone, Button, EmptyState, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { dateTimeText } from "../billing/shared";

export const CALL_TONE: Record<CallStatusEnum, BadgeTone> = {
  ringing: "info",
  answered: "success",
  missed: "danger",
  rejected: "warning",
  failed: "default",
};

export function phoneText(phone?: string | null): string {
  if (!phone) return "—";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

export function durationText(seconds: number | null | undefined, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (seconds === null || seconds === undefined) return t("calls.noDuration");
  return t("calls.duration", { minutes: Math.floor(seconds / 60), seconds: seconds % 60 });
}

/** Refresh every list and count of calls after a change. */
export function useRefreshCalls(): () => void {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/calls/") });
}

function CallIcon({ call }: { call: Call }) {
  const props = { size: 18, strokeWidth: 1.75, "aria-hidden": true } as const;
  if (call.status === "missed") return <PhoneMissed {...props} />;
  return call.direction === "out" ? <PhoneOutgoing {...props} /> : <PhoneIncoming {...props} />;
}

export function CallRow({ call, showContact = false }: { call: Call; showContact?: boolean }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshCalls();
  const take = useCallsHandledCreate({ mutation: { onSuccess: refresh } });
  const when = call.started_at ?? call.created_at;
  return (
    <li className="call-row">
      <span className={`call-row__icon call-row__icon--${call.status}`}>
        <CallIcon call={call} />
      </span>
      <div className="call-row__main">
        <div className="row">
          {showContact ? (
            <Link className="link" to={`/clients/${call.contact}`}>
              {call.contact_name || phoneText(call.phone_e164)}
            </Link>
          ) : null}
          <Badge dot tone={CALL_TONE[call.status ?? "ringing"]}>
            {t(`calls.status.${call.status ?? "ringing"}`)}
          </Badge>
          <span className="muted">{t(`calls.direction.${call.direction ?? "in"}`)}</span>
          {call.simulated ? <Badge tone="offer">{t("calls.simulated")}</Badge> : null}
        </div>
        <small className="muted">
          <time dateTime={when}>{dateTimeText(when, i18n.language)}</time>
          {call.status === "answered" ? <> · <span className="ao-num">{durationText(call.duration_seconds, t)}</span></> : null}
          {showContact ? <> · <span className="ao-num">{phoneText(call.phone_e164)}</span></> : null}
          {call.handled_by_name ? <> · {t("calls.handledBy", { name: call.handled_by_name })}</> : null}
        </small>
      </div>
      <div className="call-row__actions">
        {call.callback_url ? (
          <a className="ao-button ao-button--whatsapp ao-button--sm" href={call.callback_url} target="_blank" rel="noopener noreferrer">
            <PhoneCall size={14} aria-hidden="true" /> {t("calls.callBack")}
          </a>
        ) : null}
        {call.status === "missed" && !call.handled_at ? (
          <Button size="sm" variant="ghost" loading={take.isPending} onClick={() => take.mutate({ id: call.id })}>
            {t("calls.handle")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** The calls of one contact: a card on the contact record and in the inbox. Hidden when the
 * contact never called and the agency did not turn calls on. */
export function ContactCalls({ contactId, compact = false, limit }: { contactId: string; compact?: boolean; limit?: number }) {
  const { t } = useTranslation();
  const params: CallsListParams = { contact: contactId };
  const calls = useCallsList(params, { query: { enabled: Boolean(contactId) } });
  const summary = useCallsSummaryRetrieve();
  const all = calls.data?.status === 200 ? calls.data.data.results : [];
  const rows = limit ? all.slice(0, limit) : all;
  const enabled = summary.data?.status === 200 ? summary.data.data.enabled : false;
  if (calls.isPending) return compact ? null : <Skeleton style={{ height: 60 }} />;
  if (calls.data?.status !== 200 || (!rows.length && !enabled)) return null;
  const list = rows.length ? (
    <ul className="plain-list call-list">
      {rows.map((call) => (
        <CallRow key={call.id} call={call} />
      ))}
    </ul>
  ) : (
    <EmptyState compact icon={<PhoneIncoming size={22} strokeWidth={1.75} />} title={t("calls.empty")} />
  );
  if (compact) {
    if (!rows.length) return null; // the inbox shows the calls only when there are some
    return (
      <section className="stack call-compact" aria-label={t("calls.history")}>
        <h3>{t("calls.history")}</h3>
        {list}
      </section>
    );
  }
  return (
    <GlassCard as="section" className="stack" aria-label={t("calls.history")}>
      <h2>{t("calls.history")}</h2>
      {list}
    </GlassCard>
  );
}
